import React, { useEffect, useRef, useState } from "react";
import ReactSelect from "react-select";
import moment from "moment";
import "moment/locale/gu";
import {
  Plus,
  Search,
  Pencil,
  Trash2,
  Filter,
  ListFilter,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import { useForm, Controller } from "react-hook-form";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  fetchAccountTypesListData,
  getAccountTypes,
  getTotalAccountTypes,
  getAccountTypesFilter,
  setAccountTypesFilter,
  getAccountTypesLoading,
  setEditAccountType,
  toggleAccountTypeModal,
} from "../../features/accounts/accountTypesSlice";
import { getSelectedBranch } from "../../features/settings/settingsSlice";
import { useCanAccess } from "../../hooks/useCanAccess";
import useConfirmPopup from "../../context/useConfirmPopup";
import { DELETE, POST, PUT } from "../../services/axios.service";
import { toastStyles, filterSelectStyles } from "../../static/styles";
import { t } from "../../locales";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "../ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { AccountTypesProps, AccountTypesFilterType, IFormInput } from "../../types/accountTypes";
import AccountTypeFormModal from "./AccountTypeFormModal";

const sortByOptions = [
  { value: "id", label: t.accountTypes.sortOptions.id },
  { value: "name", label: t.accountTypes.sortOptions.name },
  { value: "created_at", label: t.accountTypes.sortOptions.created_at },
];

const sortOrderOptions = [
  { value: "asc", label: t.accountTypes.sortOrderOptions.asc },
  { value: "desc", label: t.accountTypes.sortOrderOptions.desc },
];

const AccountTypeList = () => {
  const dispatch = useAppDispatch();
  const canAccess = useCanAccess();
  const confirmDelete = useConfirmPopup();

  moment.locale("gu");

  const accountTypes = useAppSelector(getAccountTypes);
  const totalAccountTypes = useAppSelector(getTotalAccountTypes);
  const filter = useAppSelector(getAccountTypesFilter);
  const loading = useAppSelector(getAccountTypesLoading);
  const selectedBranch = useAppSelector(getSelectedBranch);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [showFilters, setShowFilters] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState<number>(0);

  const { register, control, handleSubmit, reset } =
    useForm<AccountTypesFilterType>({
      defaultValues: { ...filter },
    });

  const fetchData = () => {
    dispatch(
      fetchAccountTypesListData({
        page,
        size: pageSize,
        search: filter.search,
        sortBy: filter.sortBy,
        sortOrder: filter.sortOrder,
      })
    );
  };

  const prevBranchIdRef = useRef<number | null>(null);
  const selectedBranchId = selectedBranch?.id ?? null;

  useEffect(() => {
    if (!selectedBranchId) return;

    const prevBranchId = prevBranchIdRef.current;
    prevBranchIdRef.current = selectedBranchId;

    const isBranchChange =
      prevBranchId !== null && prevBranchId !== selectedBranchId;
    if (isBranchChange && page !== 1) {
      setPage(1);
      return;
    }

    fetchData();
  }, [page, pageSize, filter, selectedBranchId]);

  const onApplyFilters = (data: AccountTypesFilterType) => {
    dispatch(
      setAccountTypesFilter({
        search: data.search || null,
        sortBy: data.sortBy,
        sortOrder: data.sortOrder,
      })
    );
    setPage(1);
  };

  const handleClearFilters = () => {
    const defaults: AccountTypesFilterType = {
      search: null,
      sortBy: "id",
      sortOrder: "desc",
    };
    reset(defaults);
    dispatch(setAccountTypesFilter(defaults));
    setPage(1);
  };

  const handleShowForm = async () => {
    await dispatch(setEditAccountType(null));
    dispatch(toggleAccountTypeModal(true));
  };

  const handleSaveAccountType = async (data: IFormInput) => {
    try {
      const storeEdit = (await import("../../app/store")).store.getState()
        .accountTypes.editAccountType;

      if (storeEdit && storeEdit.id) {
        const response: any = await PUT(`/v1/account-types/${storeEdit.id}`, {
          name: data.name,
          phone: data.phone,
          description: data.description,
        });
        if (response.data.status === "success") {
          await dispatch(setEditAccountType(null));
          if (page === 1) fetchData();
          else setPage(1);
          toast.success(response.data.message, { ...(toastStyles as any) });
        } else {
          toast.error(response.data.message, { ...(toastStyles as any) });
        }
      } else {
        const response: any = await POST("/v1/account-types", {
          name: data.name,
          phone: data.phone,
          description: data.description,
        });
        if (response.data.status === "success") {
          if (page === 1) fetchData();
          else setPage(1);
          toast.success(response.data.message, { ...(toastStyles as any) });
        } else {
          toast.error(response.data.message, { ...(toastStyles as any) });
        }
      }
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          error?.data?.message ||
          t.common.somethingWentWrong,
        { ...(toastStyles as any) }
      );
    }
  };

  const handleDeleteAccountType = async (id: number) => {
    const response = await confirmDelete(
      t.accountTypes.deleteConfirmText,
      t.accountTypes.deleteConfirmTitle,
      t.accountTypes.deleteConfirmButton,
      true
    );

    if (response.confirmed) {
      setActionLoadingId(id);
      try {
        const res: any = await DELETE(`/v1/account-types/${id}`, {
          password: response.password,
        });
        if (res.data.status === "success") {
          toast.success(res.data.message, { ...(toastStyles as any) });
          const newPage =
            accountTypes.length > 1 ? page : Math.max(page - 1, 1);
          if (newPage === page) fetchData();
          else setPage(newPage);
        } else {
          toast.error(res.data.message, { ...(toastStyles as any) });
        }
      } catch (error: any) {
        toast.error(
          error?.response?.data?.message ||
            error?.data?.message ||
            t.common.somethingWentWrong,
          { ...(toastStyles as any) }
        );
      }
      setActionLoadingId(0);
    }
  };

  const handleEditAccountType = async (accountType: AccountTypesProps) => {
    await dispatch(setEditAccountType(accountType));
    dispatch(toggleAccountTypeModal(true));
  };

  const totalPages = Math.ceil(totalAccountTypes / pageSize);

  const getVisiblePages = (): (number | string)[] => {
    const pages: (number | string)[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (page > 3) pages.push("...");
      for (
        let i = Math.max(2, page - 1);
        i <= Math.min(totalPages - 1, page + 1);
        i++
      ) {
        pages.push(i);
      }
      if (page < totalPages - 2) pages.push("...");
      pages.push(totalPages);
    }
    return pages;
  };

  const start = totalAccountTypes === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, totalAccountTypes);

  return (
    <div className="space-y-4">
      {/* Top bar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-3 ml-auto">
          <Button
            variant="outline"
            className="h-11 px-5 rounded-lg border-gray-300 text-[#113F67] hover:bg-gray-50 hover:text-[#113F67]"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter className="h-4 w-4 mr-2" />
            {t.accountTypes.filterToggle}
          </Button>

          {canAccess("account-types.create") && (
            <Button
              className="h-11 px-5 rounded-lg bg-[#113F67] hover:bg-[#0d2f4f] border border-[#113F67]"
              onClick={handleShowForm}
            >
              <Plus className="h-4 w-4 mr-2" />
              {t.accountTypes.addNewAccountType}
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t.accountTypes.filterTitle}</CardTitle>
          </CardHeader>
          <CardContent className="pt-0 pb-4">
            <form onSubmit={handleSubmit(onApplyFilters)}>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 items-end">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    {t.accountTypes.searchLabel}
                  </label>
                  <div className="relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <Input
                      className="pl-12 h-12 text-base"
                      placeholder={t.accountTypes.searchPlaceholder}
                      {...register("search")}
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    {t.accountTypes.sortByLabel}
                  </label>
                  <div className="relative">
                    <ListFilter className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#34699A] z-10 pointer-events-none" />
                    <Controller
                      name="sortBy"
                      control={control}
                      render={({ field }) => (
                        <ReactSelect
                          styles={filterSelectStyles}
                          options={sortByOptions}
                          placeholder={t.accountTypes.sortByPlaceholder}
                          value={sortByOptions.find(
                            (o) => o.value === field.value
                          )}
                          onChange={(opt: any) =>
                            field.onChange(opt?.value || "id")
                          }
                        />
                      )}
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    {t.accountTypes.sortOrderLabel}
                  </label>
                  <div className="relative">
                    <ArrowUpDown className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#34699A] z-10 pointer-events-none" />
                    <Controller
                      name="sortOrder"
                      control={control}
                      render={({ field }) => (
                        <ReactSelect
                          styles={filterSelectStyles}
                          options={sortOrderOptions}
                          placeholder={t.accountTypes.sortOrderPlaceholder}
                          value={sortOrderOptions.find(
                            (o) => o.value === field.value
                          )}
                          onChange={(opt: any) =>
                            field.onChange(opt?.value || "desc")
                          }
                        />
                      )}
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-3 mt-4 border-t pt-4 justify-end">
                <Button
                  type="button"
                  variant="outline"
                  className="h-11 px-5 rounded-lg border-gray-300 text-[#113F67] hover:bg-gray-50 hover:text-[#113F67]"
                  onClick={handleClearFilters}
                >
                  {t.accountTypes.clearFilters}
                </Button>
                <Button
                  type="submit"
                  className="h-11 px-5 rounded-lg bg-[#113F67] hover:bg-[#0d2f4f] border border-[#113F67]"
                >
                  {t.accountTypes.applyFilters}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {loading && accountTypes.length === 0 ? (
          <Card>
            <CardContent className="py-8">
              <div className="flex items-center justify-center gap-2 text-gray-500">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#113F67]" />
                {t.accountTypes.loading}
              </div>
            </CardContent>
          </Card>
        ) : !loading && accountTypes.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center text-gray-500">
              {t.accountTypes.noRecords}
            </CardContent>
          </Card>
        ) : (
          <div
            className={`space-y-3${
              loading ? " opacity-50 pointer-events-none" : ""
            }`}
          >
            {accountTypes.map((at: AccountTypesProps, index: number) => (
              <Card key={at.id}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <span className="font-medium text-gray-900">
                        {at.name || "-"}
                      </span>
                      <div className="text-sm text-gray-500">
                        #{(page - 1) * pageSize + index + 1} · {at.phone || "-"}
                      </div>
                    </div>
                    <div>
                      {actionLoadingId === at.id ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#113F67]" />
                      ) : (
                        <div className="flex items-center gap-1">
                          {canAccess("account-types.update") && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => handleEditAccountType(at)}
                            >
                              <Pencil className="h-4 w-4 text-[#113F67]" />
                            </Button>
                          )}
                          {canAccess("account-types.delete") && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => handleDeleteAccountType(at.id)}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="mt-3 pt-3 border-t space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">{t.accountTypes.table.totalIncome}</span>
                      <span className="text-green-600 font-medium">
                        {at?.credit_amount?.toFixed(2) || "0.00"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">{t.accountTypes.table.totalExpenses}</span>
                      <span className="text-red-600 font-medium">
                        {at?.debit_amount?.toFixed(2) || "0.00"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">{t.accountTypes.table.date}</span>
                      <span className="text-gray-700">
                        {moment(at.created_at).format("DD MMMM, YYYY hh:mm A")}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Table */}
      <Card className="hidden md:block">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">{t.accountTypes.table.srNo}</TableHead>
                  <TableHead>{t.accountTypes.table.name}</TableHead>
                  <TableHead>{t.accountTypes.table.phone}</TableHead>
                  <TableHead>{t.accountTypes.table.totalIncome}</TableHead>
                  <TableHead>{t.accountTypes.table.totalExpenses}</TableHead>
                  <TableHead>{t.accountTypes.table.description}</TableHead>
                  <TableHead>{t.accountTypes.table.date}</TableHead>
                  <TableHead className="text-right">{t.accountTypes.table.actions}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8">
                      <div className="flex items-center justify-center gap-2 text-gray-500">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#113F67]" />
                        {t.accountTypes.loading}
                      </div>
                    </TableCell>
                  </TableRow>
                ) : accountTypes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                      {t.accountTypes.noRecords}
                    </TableCell>
                  </TableRow>
                ) : (
                  accountTypes.map((at: AccountTypesProps, index: number) => (
                    <TableRow key={at.id}>
                      <TableCell className="font-medium text-gray-500">
                        {(page - 1) * pageSize + index + 1}
                      </TableCell>
                      <TableCell>
                        <span className="font-medium text-gray-900">{at.name || "-"}</span>
                      </TableCell>
                      <TableCell className="text-gray-600">{at.phone || "-"}</TableCell>
                      <TableCell className="text-green-600 font-medium">
                        {at?.credit_amount?.toFixed(2) || "0.00"}
                      </TableCell>
                      <TableCell className="text-red-600 font-medium">
                        {at?.debit_amount?.toFixed(2) || "0.00"}
                      </TableCell>
                      <TableCell className="text-gray-600 max-w-[200px] truncate">
                        {at?.description || "-"}
                      </TableCell>
                      <TableCell className="text-gray-500 text-sm">
                        {moment(at.created_at).format("DD MMMM, YYYY hh:mm A")}
                      </TableCell>
                      <TableCell className="text-right">
                        {actionLoadingId === at.id ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#113F67] ml-auto" />
                        ) : (
                          <div className="flex items-center justify-end gap-1">
                            {canAccess("account-types.update") && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => handleEditAccountType(at)}
                              >
                                <Pencil className="h-4 w-4 text-[#113F67]" />
                              </Button>
                            )}
                            {canAccess("account-types.delete") && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => handleDeleteAccountType(at.id)}
                              >
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            )}
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Pagination */}
      {!loading && accountTypes.length > 0 && (
        <div className="flex flex-wrap items-center justify-end md:justify-between gap-4">
          <div className="hidden md:flex items-center gap-2 text-sm text-gray-600">
            <span>
              {start}-{end} of {totalAccountTypes} {t.accountTypes.pageOfLabel}
            </span>
            <span>{t.accountTypes.pageSizeLabel}</span>
            <Select
              value={String(pageSize)}
              onValueChange={(val) => {
                setPageSize(Number(val));
                setPage(1);
              }}
            >
              <SelectTrigger className="w-20 h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-1">
            <Button variant="outline" className="h-8 w-8 p-0" onClick={() => setPage(1)} disabled={page === 1}>
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" className="h-8 w-8 p-0" onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            {getVisiblePages().map((p, i) =>
              typeof p === "string" ? (
                <span key={`ellipsis-${i}`} className="px-2 text-gray-400">...</span>
              ) : (
                <Button
                  key={p}
                  variant={page === p ? "default" : "outline"}
                  className={`w-8 h-8 p-0 ${page === p ? "bg-[#113F67] hover:bg-[#0d2f4d]" : ""}`}
                  onClick={() => setPage(p)}
                >
                  {p}
                </Button>
              )
            )}
            <Button variant="outline" className="h-8 w-8 p-0" onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page === totalPages}>
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button variant="outline" className="h-8 w-8 p-0" onClick={() => setPage(totalPages)} disabled={page === totalPages}>
              <ChevronsRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Form Modal */}
      <AccountTypeFormModal handleSaveAccountType={handleSaveAccountType} />
    </div>
  );
};

export default AccountTypeList;
